/**
 * Requirements slice (PX-W7.2, TASK-60028F48) — the project's requirements
 * register: the list read (filters ARE the cache key, so a chip change is a
 * genuine refetch and re-selecting the same chips is not), the three
 * lifecycle verbs (confirm / reject / supersede), the form-edited update,
 * and the version-scoped bulk import.
 *
 * **Eight exports are BINDING on TASK-FD4686D1** (ownership map §E):
 * `openRequirements`, `selectRequirementRows`, `selectRequirementsLoading`,
 * `selectRequirementsError`, `selectRequirementsBlocked`,
 * `selectFitGapSummary`, `selectRequirementsTotalEffort`,
 * `requirementsClosed`. Do not rename any of these.
 *
 * Mutation model (PX-W6.3 idiom): a mutation thunk swaps the server-returned
 * `RequirementRow` into `rows` BY `ref` — it never re-derives state and never
 * refetches the list to learn the outcome of its own write (INV-2.4). A
 * refusal is scoped to the acting ref and disturbs no other row (INV-2.4/
 * INV-2.5). `selectFitGapSummary` / `selectRequirementsTotalEffort` are pure
 * `createSelector`s over `rows` — no count or total is ever written into
 * state (INV-2.3).
 */

import { createAction, createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { requirementsService } from '../services/requirementsService.http'
import type {
  BulkImportResult,
  FeatureBlocked,
  FitGapSummary,
  KeyedFeatureError,
  RequirementConfirmInput,
  RequirementFilters,
  RequirementRejectInput,
  RequirementRow,
  RequirementSupersedeInput,
  RequirementUpdateInput
} from '../types'

export interface RequirementsState extends FetchCache {
  projectId: string | null
  rows: RequirementRow[]
  filters: RequirementFilters
  bulkImport: BulkImportResult | null
  /** The ref of the requirement whose mutation is currently in flight. */
  mutating: string | null
  /** A mutation refusal or a non-403/404 list-read failure, scoped by `ref` when it is one. */
  error: KeyedFeatureError | null
  /** A 403/404 on the primary list read — renders the shared blocked surface, never an empty register. */
  blocked: FeatureBlocked | null
  invalidationNonce: number
}

const initialState: RequirementsState = {
  projectId: null,
  rows: [],
  filters: {},
  bulkImport: null,
  mutating: null,
  error: null,
  blocked: null,
  invalidationNonce: 0,
  ...initialFetchCache
}

interface ProjectArg {
  orgId: string
  projectId: string
}

/** The fetch-cache key: project + the EXACT filter set — a chip change is a genuine refetch. */
export const cacheKeyOf = (projectId: string, filters: RequirementFilters = {}): string =>
  `${projectId}|${JSON.stringify(filters)}`

/** Map any thrown value to the slice's keyed refusal shape. Nobody parses a body by hand or hard-codes a status. */
const toReqError = (error: unknown, scope?: string): KeyedFeatureError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { scope, code: apiError.backendKey, message: apiError.message, status: apiError.status }
}

/** Replace (or append) one row by `ref` — the swap-in every mutation shares. */
const swapRow = (state: RequirementsState, row: RequirementRow): void => {
  const index = state.rows.findIndex((r) => r.ref === row.ref)
  if (index === -1) {
    state.rows.push(row)
  } else {
    state.rows[index] = row
  }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Mount / filter change: the register's rows (`GET …/requirements`). */
export const openRequirements = createAsyncThunk<
  RequirementRow[],
  ProjectArg & { filters?: RequirementFilters },
  { rejectValue: KeyedFeatureError }
>(
  'requirements/open',
  async ({ orgId, projectId, filters }, { rejectWithValue }) => {
    try {
      return await requirementsService.listRequirements(orgId, projectId, filters ?? {})
    } catch (error) {
      return rejectWithValue(toReqError(error))
    }
  },
  {
    condition: ({ projectId, filters }, { getState }) => {
      const { requirements } = getState() as { requirements: RequirementsState }
      return shouldFetch(requirements, cacheKeyOf(projectId, filters))
    },
    // A vetoed open is still an OPEN: it dispatches `rejected` with
    // `meta.condition`, and the reducer re-asserts the project and filters on screen
    // (TASK-3D6DB8B6 fix cycle 3 — see the rejected case). No request is sent.
    dispatchConditionRejection: true
  }
)

export const confirmRequirement = createAsyncThunk<
  RequirementRow,
  ProjectArg & { ref: string; body?: RequirementConfirmInput },
  { rejectValue: KeyedFeatureError }
>('requirements/confirm', async ({ orgId, projectId, ref, body }, { rejectWithValue }) => {
  try {
    return await requirementsService.confirmRequirement(orgId, projectId, ref, body ?? {})
  } catch (error) {
    return rejectWithValue(toReqError(error, ref))
  }
})

export const rejectRequirement = createAsyncThunk<
  RequirementRow,
  ProjectArg & { ref: string; body: RequirementRejectInput },
  { rejectValue: KeyedFeatureError }
>('requirements/reject', async ({ orgId, projectId, ref, body }, { rejectWithValue }) => {
  try {
    return await requirementsService.rejectRequirement(orgId, projectId, ref, body)
  } catch (error) {
    return rejectWithValue(toReqError(error, ref))
  }
})

export const supersedeRequirement = createAsyncThunk<
  RequirementRow,
  ProjectArg & { ref: string; body?: RequirementSupersedeInput },
  { rejectValue: KeyedFeatureError }
>('requirements/supersede', async ({ orgId, projectId, ref, body }, { rejectWithValue }) => {
  try {
    return await requirementsService.supersedeRequirement(orgId, projectId, ref, body ?? {})
  } catch (error) {
    return rejectWithValue(toReqError(error, ref))
  }
})

export const updateRequirement = createAsyncThunk<
  RequirementRow,
  ProjectArg & { ref: string; input: RequirementUpdateInput },
  { rejectValue: KeyedFeatureError }
>('requirements/update', async ({ orgId, projectId, ref, input }, { rejectWithValue }) => {
  try {
    return await requirementsService.updateRequirement(orgId, projectId, ref, input)
  } catch (error) {
    return rejectWithValue(toReqError(error, ref))
  }
})

/**
 * Import a version's concluded intakes. Posts `{ versionId }` and nothing
 * else — there is no knob and no paste-rows form. Only when `created > 0`
 * does it re-arm the cache and re-dispatch {@link openRequirements} with the
 * currently active filters; a run that created nothing leaves the loaded
 * rows exactly as they were.
 */
export const bulkImportRequirements = createAsyncThunk<
  BulkImportResult,
  ProjectArg & { versionId: string },
  { rejectValue: KeyedFeatureError }
>(
  'requirements/bulkImport',
  async ({ orgId, projectId, versionId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await requirementsService.bulkImportRequirements(orgId, projectId, { versionId })
      if (result.created > 0) {
        const { filters } = (getState() as { requirements: RequirementsState }).requirements
        // Not an optimistic write: re-arm the cache so the refetch below
        // passes openRequirements's own condition (a warm cache would skip it).
        dispatch(requirementsSlice.actions.requirementsRefetchArmed())
        await dispatch(openRequirements({ orgId, projectId, filters }))
      }
      return result
    } catch (error) {
      // Deferred finding c2-1 (TASK-71426B1C): scoped 'bulk-import' — an
      // unscoped rejection here used to be indistinguishable from a
      // list-read failure and replaced the whole register with `ErrorPanel`.
      return rejectWithValue(toReqError(error, 'bulk-import'))
    }
  }
)

// ---------------------------------------------------------------------------
// Bus frame alias — SAME action type as the realtime nonce, payload-carrying
// (the "two reducers, one event" fan-out `EVENT_TYPE_MAP` documents: the
// realtime slice's own reducer bumps its nonce, and this slice separately
// `addCase`s the same action type to read the frame and guard on project).
// ---------------------------------------------------------------------------

export const requirementsBusFrame = createAction<{ projectId: string | null }>(
  'realtime/invalidateActivity'
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const requirementsSlice = createSlice({
  name: 'requirements',
  initialState,
  reducers: {
    /** Leave the workspace — resets everything. */
    requirementsClosed(state) {
      state.projectId = null
      state.rows = []
      state.filters = {}
      state.bulkImport = null
      state.mutating = null
      state.error = null
      state.blocked = null
      state.invalidationNonce = 0
      invalidate(state)
    },
    /**
     * Dismiss a refusal. `undefined` clears any error (the list-read Retry
     * in `RequirementsRegisterPanel.tsx`); a scope (a requirement ref, or
     * `'bulk-import'`) clears only an error carrying that exact scope, so
     * closing one row's reject/supersede form never wipes another row's — or
     * the bulk-import panel's — still-visible refusal (TASK-A274D065, C-9).
     */
    requirementsErrorDismissed(state, action: PayloadAction<string | undefined>) {
      if (action.payload === undefined || state.error?.scope === action.payload) {
        state.error = null
      }
    },
    /**
     * Re-arm the fetch cache ahead of a refetch. Internal use (a
     * mutation-triggered refetch) AND the register's Retry control (deferred
     * finding c1-2, TASK-71426B1C) — `requirementsErrorDismissed` alone does
     * not invalidate, so `openRequirements`'s own cache condition
     * (`loadedKey` unchanged, `invalidated: false` after a failed fetch)
     * skipped the re-dispatch and Retry was a silent no-op. A server change:
     * a re-arm during an in-flight load earns one follow-up (TASK-3D6DB8B6).
     */
    requirementsRefetchArmed(state) {
      markServerChanged(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(openRequirements.pending, (state, action) => {
        if (state.projectId !== action.meta.arg.projectId) {
          state.rows = []
        }
        fetchStarted(state, cacheKeyOf(action.meta.arg.projectId, action.meta.arg.filters))
        state.projectId = action.meta.arg.projectId
        state.filters = action.meta.arg.filters ?? {}
        state.error = null
        state.blocked = null
      })
      .addCase(openRequirements.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded (project, filters) reply writes nothing.
        if (!fetchSucceeded(state, cacheKeyOf(action.meta.arg.projectId, action.meta.arg.filters))) return
        state.rows = action.payload ?? []
      })
      .addCase(openRequirements.rejected, (state, action) => {
        if (action.meta.condition) {
          // A VETOED open (TASK-3D6DB8B6 fix cycle 3, the projectDetail
          // precedent): this (project, filters) load is in flight or fresh.
          // `requirementsClosed` (StrictMode's between-effects cleanup, or a
          // remount mid-load) nulled `projectId`, and the frame guard below
          // then dropped every frame. Re-assert it; the cache is untouched
          // and only an explicit open reaches this branch — never the
          // `toReqError` fallback, so a veto is never shown as an error.
          state.projectId = action.meta.arg.projectId
          state.filters = action.meta.arg.filters ?? {}
          return
        }
        if (!fetchFailed(state, cacheKeyOf(action.meta.arg.projectId, action.meta.arg.filters))) return
        const err = action.payload ?? toReqError(action.error)
        if (err.status === 403 || err.status === 404) {
          state.blocked = { status: err.status, code: err.code, message: err.message }
          state.error = null
        } else {
          state.error = err
          state.blocked = null
        }
      })
      .addCase(confirmRequirement.pending, (state, action) => {
        state.mutating = action.meta.arg.ref
        state.error = null
      })
      .addCase(confirmRequirement.fulfilled, (state, action) => {
        state.mutating = null
        swapRow(state, action.payload)
      })
      .addCase(confirmRequirement.rejected, (state, action) => {
        state.mutating = null
        state.error = action.payload ?? toReqError(action.error, action.meta.arg.ref)
      })
      .addCase(rejectRequirement.pending, (state, action) => {
        state.mutating = action.meta.arg.ref
        state.error = null
      })
      .addCase(rejectRequirement.fulfilled, (state, action) => {
        state.mutating = null
        swapRow(state, action.payload)
      })
      .addCase(rejectRequirement.rejected, (state, action) => {
        state.mutating = null
        state.error = action.payload ?? toReqError(action.error, action.meta.arg.ref)
      })
      .addCase(supersedeRequirement.pending, (state, action) => {
        state.mutating = action.meta.arg.ref
        state.error = null
      })
      .addCase(supersedeRequirement.fulfilled, (state, action) => {
        state.mutating = null
        swapRow(state, action.payload)
      })
      .addCase(supersedeRequirement.rejected, (state, action) => {
        state.mutating = null
        state.error = action.payload ?? toReqError(action.error, action.meta.arg.ref)
      })
      .addCase(updateRequirement.pending, (state, action) => {
        state.mutating = action.meta.arg.ref
        state.error = null
      })
      .addCase(updateRequirement.fulfilled, (state, action) => {
        state.mutating = null
        swapRow(state, action.payload)
      })
      .addCase(updateRequirement.rejected, (state, action) => {
        state.mutating = null
        state.error = action.payload ?? toReqError(action.error, action.meta.arg.ref)
      })
      .addCase(bulkImportRequirements.pending, (state) => {
        // Deferred finding c2-2 (TASK-71426B1C): clear ONLY this channel's own
        // refusal — an unconditional clear used to silently dismiss a
        // row-scoped verdict refusal `RequirementActions` was still rendering.
        if (state.error?.scope === 'bulk-import') {
          state.error = null
        }
      })
      .addCase(bulkImportRequirements.fulfilled, (state, action) => {
        state.bulkImport = action.payload
      })
      .addCase(bulkImportRequirements.rejected, (state, action) => {
        state.error = action.payload ?? toReqError(action.error, 'bulk-import')
      })
      .addCase(requirementsBusFrame, (state, action) => {
        // Refetch-on-invalidate only (I-9): another project's frame — or a
        // user-level row with no project tuple — never disturbs this feed.
        // A server change: mid-flight it earns one follow-up load.
        if (action.payload.projectId !== null && action.payload.projectId === state.projectId) {
          state.invalidationNonce += 1
          markServerChanged(state)
        }
      })
  }
})

export const { requirementsClosed, requirementsErrorDismissed, requirementsRefetchArmed } =
  requirementsSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithRequirements {
  requirements: RequirementsState
}

export const selectRequirementRows = (s: WithRequirements): RequirementRow[] => s.requirements.rows
export const selectRequirementsLoading = (s: WithRequirements): boolean => s.requirements.loading
export const selectRequirementsError = (s: WithRequirements): KeyedFeatureError | null => s.requirements.error
export const selectRequirementsBlocked = (s: WithRequirements): FeatureBlocked | null => s.requirements.blocked
export const selectRequirementFilters = (s: WithRequirements): RequirementFilters => s.requirements.filters
export const selectBulkImportResult = (s: WithRequirements): BulkImportResult | null => s.requirements.bulkImport
export const selectRequirementsNonce = (s: WithRequirements): number => s.requirements.invalidationNonce
export const selectRequirementsMutating = (s: WithRequirements): string | null => s.requirements.mutating

/** AC-3/AC-2 — the fit-gap roll-up. `createSelector` over the loaded rows only; nothing here is ever stored (INV-2.3). */
export const selectFitGapSummary = createSelector(
  [selectRequirementRows],
  (rows): FitGapSummary => ({
    total: rows.length,
    confirmed: rows.filter((r) => r.status === 'CONFIRMED').length,
    proposed: rows.filter((r) => r.status === 'PROPOSED').length,
    rejected: rows.filter((r) => r.status === 'REJECTED').length,
    superseded: rows.filter((r) => r.status === 'SUPERSEDED').length,
    byVerdict: {
      STANDARD: rows.filter((r) => r.fitGapVerdict === 'STANDARD').length,
      LIBRARY: rows.filter((r) => r.fitGapVerdict === 'LIBRARY').length,
      NEW: rows.filter((r) => r.fitGapVerdict === 'NEW').length,
      UNCLASSIFIED: rows.filter((r) => r.fitGapVerdict === 'UNCLASSIFIED').length
    }
  })
)

/**
 * AC-3 — the total FIRM-side sizing estimate. `null` when every loaded row's
 * `effortHours` is absent (the CLIENT-side redaction, or an empty register) —
 * NEVER `0`, which would read as "sized at zero hours" (INV-2.6).
 */
export const selectRequirementsTotalEffort = createSelector(
  [selectRequirementRows],
  (rows): number | null =>
    rows.every((r) => r.effortHours === undefined)
      ? null
      : rows.reduce((sum, r) => sum + (r.effortHours ?? 0), 0)
)

/**
 * Whether the loaded register holds the row a `?ref=` link names (the board's
 * requirement chips, CR-BUILD-BOARD-EPICS-2026-09-26). Added additively by
 * TASK-41CE1DE2 fix cycle 1 (review low: the lookup was an inline `rows.some`
 * in `RequirementsRegisterPanel`) — this slice is TASK-60028F48's.
 */
export const selectRequirementRowLoaded = createSelector(
  [selectRequirementRows, (_s: WithRequirements, ref: string | null) => ref],
  (rows, ref): boolean => ref !== null && rows.some((row) => row.ref === ref)
)
